import { useContext } from 'react';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import { IconType } from '../../../../../../BaseComponents/Icon/Utils/IconDefinitions';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDLink from '../../../../../../BaseComponents/Link/EDLink';
import { SchedulerContext } from './SchedulerContext';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { sortKalenderResources } from './SchedulerComponent/Utils/resourcesSortUtils';

export interface IProps {
  activityId?: string;
  bookingTimeStart?: string | number | Date | undefined;
  bookingTimeEnd?: string | number | Date | undefined;
  locations?: IKalenderResourceTooltipInfo[];
  persons?: IKalenderResourceTooltipInfo[];
  otherResources?: IKalenderResourceTooltipInfo[];
  externalNotes?: string;
  internalNotes?: string;
  links?: IKalenderLinkTooltipInfo[];
  IconType?: IconType;
  resursNamn?: string | null;
  bookinSignature?: string | null;
  repCount?: number | null | undefined;
  bokadAv?: string | null;
  bevakning?: boolean | null | undefined;
  bevakningtText?: string | null | undefined;
  externPublicering?: boolean | null | undefined;
  begravningsordning?: string | null | undefined;
  bookingType?: string | undefined;
}

export interface IKalenderResourceTooltipInfo {
  resourceName?: string | null;
  resourceTypeName?: string | null;
  stallTidFran?: string | number | Date | undefined;
  stallTidTill?: string | number | Date | undefined;
  prio_ordning?: number | null | undefined;
  aktResRub_PosNr?: number | null;
  resourceTitle?: string | null;
  bo_resurs_id?: string | undefined;
  begravningsordning?: string | null | undefined;
}

export interface IKalenderLinkTooltipInfo {
  linkName?: string;
  linkTidFran?: string | number | Date | undefined;
  linkTidTill?: string | number | Date | undefined;
  linkInfo?: string;
  linkLocation?: string;
}

interface IKalenderTooltipItemProps extends IKalenderResourceTooltipInfo {
  iconType?: IconType;
  paddingTop?: number;
  activityId?: string;
  bookingTimeStart?: string | number | Date | undefined;
  bookingTimeEnd?: string | number | Date | undefined;
  externalNotes?: string | null | undefined;
  internalNotes?: string | null | undefined;
  linkInfo?: string | null;
  linkLocation?: string | null;
  bookingSignature?: string | undefined;
  repCount?: number | null | undefined;
  bokadAv?: string;
  numberOfResources?: number;
  iconTypeName?: string;
  isBevakad?: boolean | undefined;
  bevakninText?: string | null | undefined;
  isExternPublicering?: boolean | null | undefined;
}

const KalenderTooltipItem = (props: IKalenderTooltipItemProps) => {
  const schedulerContext = useContext(SchedulerContext);
  const isStallTid = () => {
    if (
      props.stallTidFran !== props.bookingTimeStart ||
      props.stallTidTill !== props.bookingTimeEnd
    ) {
      return true;
    }

    return false;
  };

  const aktLink = (onSelectDetail: (_: string) => void, activityId: string) => (
    <span>
      <EDLink
        onClick={() => {
          onSelectDetail(activityId);
        }}
      >
        {props.resourceName}
      </EDLink>
    </span>
  );

  return (
    <EDGrid
      style={{ display: 'flex', paddingTop: props.paddingTop ?? '' }}
      container
    >
      <EDGrid style={{ display: 'flex' }} xs={2} item>
        {props.iconType !== undefined ? (
          <EDIcon paddingLeft={2.5} iconType={props.iconType} />
        ) : null}
      </EDGrid>
      <EDGrid
        xs={10}
        style={{
          display: 'inline-flex',
          alignItems: props.paddingTop === 16 ? 'end' : undefined,
        }}
        item
      >
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
          }}
        >
          {props.resourceName !== undefined &&
          props.activityId !== undefined &&
          schedulerContext.onSelectDetail !== undefined ? (
            aktLink(schedulerContext.onSelectDetail, props.activityId)
          ) : props.resourceName !== undefined && isStallTid() ? (
            <span>
              {props.resourceName}
              {props.resourceTypeName && (
                <strong> ({props.resourceTypeName}) </strong>
              )}
              {!props.linkInfo && (
                <>{`${props.stallTidFran}-${props.stallTidTill}`}</>
              )}
            </span>
          ) : props.resourceName !== undefined ? (
            <EDBox sx={{ display: 'flex' }}>
              {props.resourceName}
              {props.resourceTypeName && (
                <EDTypography marginLeft={0.5} variant="subtitle2">
                  ({props.resourceTypeName})
                </EDTypography>
              )}
            </EDBox>
          ) : props.numberOfResources ? (
            <EDBox sx={{ display: 'flex' }}>
              +{props.numberOfResources - 2}{' '}
              {props.iconTypeName === 'room'
                ? 'platser'
                : props.iconTypeName === 'group'
                ? 'personer'
                : props.iconTypeName === 'post_add'
                ? 'övriga resurser'
                : null}
            </EDBox>
          ) : null}

          {props.isBevakad ? (
            <EDBox
              sx={{
                display: '-webkit-box',
                '-webkit-box-orient': 'vertical',
                '-webkit-line-clamp': '2',
                overflow: 'hidden',
              }}
            >
              <strong>Bevakad: </strong> {props.bevakninText}
            </EDBox>
          ) : null}

          {props.isExternPublicering ? (
            <EDBox>
              <EDTypography>Publiceras på hemsidans kalender</EDTypography>
            </EDBox>
          ) : null}

          {props.externalNotes ? (
            <EDBox>
              <EDBox
                sx={{
                  display: '-webkit-box',
                  '-webkit-box-orient': 'vertical',
                  '-webkit-line-clamp': '2',
                  overflow: 'hidden',
                }}
              >
                <strong>Extern not: </strong>
                {props.externalNotes}
              </EDBox>
            </EDBox>
          ) : null}

          {props.internalNotes && props.externalNotes ? (
            <EDBox
              sx={{
                display: '-webkit-box',
                '-webkit-box-orient': 'vertical',
                '-webkit-line-clamp': '2',
                overflow: 'hidden',
                paddingTop: 0.5,
              }}
            >
              <strong>Intern not: </strong>
              {props.internalNotes}
            </EDBox>
          ) : null}

          {props.internalNotes && !props.externalNotes ? (
            <EDBox
              sx={{
                display: '-webkit-box',
                '-webkit-box-orient': 'vertical',
                '-webkit-line-clamp': '2',
                overflow: 'hidden',
                paddingTop: undefined,
              }}
            >
              <strong>Intern not: </strong>
              {props.internalNotes}
            </EDBox>
          ) : null}

          {props.linkInfo ? <span>{props.linkInfo}</span> : null}
          {props.linkLocation !== undefined ? (
            <span>{props.linkLocation}</span>
          ) : null}

          {props.repCount && props.repCount > 0 ? (
            <span>Repeterad {props.repCount} gånger</span>
          ) : null}
          {props.bokadAv ? (
            <span data-testId={'BokadAv'}>Bokad av: {props.bokadAv}</span>
          ) : null}
          {props.begravningsordning ? (
            <span>Begravningsordning: {props.begravningsordning}</span>
          ) : null}
        </div>
      </EDGrid>
    </EDGrid>
  );
};

const KalenderTooltipComponent = (props: IProps) => {
  const resItems = (
    items: IKalenderResourceTooltipInfo[] | undefined,
    iconType: IconType
  ) => {
    if (items && items.length > 0) {
      let hasRenderedIcon = false;

      return items.map((item, index) => {
        if (!item.resourceName) {
          return null;
        }
        if (index > 2) {
          if (items.length === index + 1 && index) {
            return (
              <KalenderTooltipItem
                key={index}
                numberOfResources={index}
                iconTypeName={iconType}
              />
            );
          } else return null;
        } else {
          if (!hasRenderedIcon) {
            hasRenderedIcon = true;

            return (
              <KalenderTooltipItem
                key={item.resourceName}
                bookingTimeStart={props.bookingTimeStart}
                bookingTimeEnd={props.bookingTimeEnd}
                stallTidFran={item.stallTidFran}
                stallTidTill={item.stallTidTill}
                resourceTypeName={item.resourceTypeName}
                resourceName={item.resourceName}
                paddingTop={16}
                iconType={iconType}
              />
            );
          } else {
            return (
              <KalenderTooltipItem
                key={item.resourceName}
                bookingTimeStart={props.bookingTimeStart}
                bookingTimeEnd={props.bookingTimeEnd}
                stallTidFran={item.stallTidFran}
                stallTidTill={item.stallTidTill}
                resourceTypeName={item.resourceTypeName}
                resourceName={item.resourceName}
              />
            );
          }
        }
      });
    } else return null;
  };

  const notesItems = (
    externalNotes: string | undefined,
    internalNotes: string | undefined
  ) => {
    if (externalNotes || internalNotes) {
      return (
        <KalenderTooltipItem
          key={'key'}
          paddingTop={20}
          iconType="notes"
          externalNotes={externalNotes}
          internalNotes={internalNotes}
        />
      );
    } else return null;
  };

  const linkItems = (items: IKalenderLinkTooltipInfo[] | undefined) => {
    if (items && items.length > 0) {
      let hasRenderedIcon = false;

      return items.map(item => {
        if (!hasRenderedIcon) {
          hasRenderedIcon = true;
          return (
            <KalenderTooltipItem
              key={item.linkName}
              bookingTimeStart={props.bookingTimeStart}
              bookingTimeEnd={props.bookingTimeEnd}
              stallTidFran={item.linkTidFran}
              stallTidTill={item.linkTidTill}
              resourceName={item.linkName}
              linkLocation={item.linkLocation}
              linkInfo={item.linkInfo}
              paddingTop={10}
              iconType="link"
            />
          );
        } else
          return (
            <KalenderTooltipItem
              key={item.linkName}
              bookingTimeStart={props.bookingTimeStart}
              bookingTimeEnd={props.bookingTimeEnd}
              stallTidFran={item.linkTidFran}
              stallTidTill={item.linkTidTill}
              resourceName={item.linkName}
              linkLocation={item.linkLocation}
              linkInfo={item.linkInfo}
              paddingTop={16}
            />
          );
      });
    } else return null;
  };

  const repCount = (repCount: number | null | undefined) => {
    if (repCount) {
      return (
        <KalenderTooltipItem
          key={'key'}
          iconType="repeated_event"
          paddingTop={20}
          repCount={repCount}
        />
      );
    } else return null;
  };

  const bokadAv = (bokadAv: string | null | undefined) => {
    if (bokadAv) {
      return (
        <KalenderTooltipItem key={'key'} paddingTop={20} bokadAv={bokadAv} />
      );
    } else return null;
  };

  const begravningsordning = (
    begravningsordning: string | null | undefined
  ) => {
    if (begravningsordning) {
      return (
        <KalenderTooltipItem
          key={'key'}
          paddingTop={20}
          begravningsordning={begravningsordning}
        />
      );
    } else return null;
  };

  const bevkningItems = (
    isBevakad: boolean | null | undefined,
    bevakningText: string | null | undefined,
    iconType: IconType
  ) => {
    if (isBevakad) {
      return (
        <KalenderTooltipItem
          key={'key'}
          paddingTop={24}
          iconType={iconType}
          isBevakad={isBevakad}
          bevakninText={bevakningText}
        />
      );
    } else return null;
  };

  const externPublicering = (
    externPublicering: boolean | null | undefined,
    iconType: IconType
  ) => {
    if (externPublicering) {
      return (
        <KalenderTooltipItem
          key={'key'}
          isExternPublicering={externPublicering}
          iconType={iconType}
          paddingTop={10}
        />
      );
    } else return null;
  };

  return (
    <div>
      <EDGrid>
        {resItems(sortKalenderResources(props.locations), 'room')}
      </EDGrid>
      <EDGrid>{resItems(sortKalenderResources(props.persons), 'group')}</EDGrid>
      <EDGrid>
        {resItems(sortKalenderResources(props.otherResources), 'post_add')}
      </EDGrid>
      <EDGrid>
        {bevkningItems(props.bevakning, props.bevakningtText, 'visibility')}
      </EDGrid>
      <EDGrid>{externPublicering(props.externPublicering, 'public')}</EDGrid>
      <EDGrid>{notesItems(props.externalNotes, props.internalNotes)}</EDGrid>
      <EDGrid>{linkItems(props.links)}</EDGrid>
      <EDGrid>{repCount(props.repCount)}</EDGrid>
      {props.bookingType === 'BEGRAVNING' && (
        <EDGrid>{begravningsordning(props.begravningsordning ?? '-')}</EDGrid>
      )}

      <EDGrid>{bokadAv(props.bokadAv)}</EDGrid>
    </div>
  );
};

export default KalenderTooltipComponent;
